import { getDeterministicIndexForDate } from './deterministic';

/**
 * Deterministic, date-seeded pick from the local question list so every client
 * chooses the same question for a given day and it is stable across reloads.
 *
 * `today` (a YYYY-MM-DD string) and `getQuestionFromId(id)` are injected by the
 * caller so this stays platform-agnostic: each app computes "today" with its own
 * date util (web moment-timezone vs mobile moment — must not be unified) and
 * provides its own content lookup.
 *
 * @param {{ questionsList: Array<{id: any}>, getQuestionFromId: (id:any)=>any, today: string }} args
 * @returns {{ qotdId: any, question: any }}
 */
export function pickDeterministicQuestion({ questionsList, getQuestionFromId, today }) {
  const index = getDeterministicIndexForDate(today, questionsList.length);
  const selected = questionsList[index];
  return { qotdId: selected.id, question: getQuestionFromId(selected.id) };
}

/**
 * Resolve today's Question of the Day — the single, provider-agnostic decision
 * state machine shared by web and mobile. Firestore I/O is INJECTED (apps keep
 * their own reads/writes); this function never touches an SDK.
 *
 * Returns one of:
 *   { state: 'loading' }      questions not yet available locally
 *   { state: 'unavailable' }  shared-doc fetch failed (offline) — caller must NOT
 *                             fabricate or record a question; show retry
 *   { state: 'ready', qotdId, question }  resolved question to display/answer
 *
 * @param {Object} args
 * @param {Array<{id:any}>} args.questionsList   local question list for this build
 * @param {(id:any)=>any}   args.getQuestionFromId  content lookup
 * @param {string}          args.today           YYYY-MM-DD (each app's own getTodayLocal)
 * @param {()=>Promise<{data?:{question_id?:any}, isError?:boolean}>} args.fetchSharedDoc
 *        reads apps/{APP}/qotd/{today}; returns { isError:true } on network failure
 * @param {(qotdId:any)=>Promise<any>} args.createSharedDoc
 *        create-if-absent write of apps/{APP}/qotd/{today}
 */
export async function RESOLVE_TODAY_QOTD({
  questionsList,
  getQuestionFromId,
  today,
  fetchSharedDoc,
  createSharedDoc,
}) {
  if (!questionsList || questionsList.length === 0) {
    return { state: 'loading', qotdId: null, question: null };
  }

  const result = await fetchSharedDoc();

  // Network/fetch error — do not invent a question.
  if (!result || result.isError) {
    return { state: 'unavailable', qotdId: null, question: null };
  }

  // Canonical doc exists for today — use its question id.
  if (result.data?.question_id != null) {
    const qotdId = result.data.question_id;
    const question = getQuestionFromId(qotdId);
    if (question?.question) {
      return { state: 'ready', qotdId, question };
    }
    // Doc exists but the question isn't in this build's local content (rare).
    // Resolve a deterministic local candidate for display only — never
    // overwrite the shared canonical doc.
    const fallback = pickDeterministicQuestion({ questionsList, getQuestionFromId, today });
    return { state: 'ready', qotdId: fallback.qotdId, question: fallback.question };
  }

  // Doc is genuinely absent — create it deterministically (create-if-absent).
  const { qotdId, question } = pickDeterministicQuestion({ questionsList, getQuestionFromId, today });
  await createSharedDoc(qotdId);
  return { state: 'ready', qotdId, question };
}
